/**
 * Client-side image moderation using nsfwjs (TensorFlow.js).
 * The model is lazy-loaded on first use (~5MB from CDN), so it won't
 * affect initial bundle or page load. Subsequent checks are instant.
 */

import type { NSFWJS } from "nsfwjs";

let modelPromise: Promise<NSFWJS> | null = null;

const loadModel = async (): Promise<NSFWJS> => {
  if (!modelPromise) {
    modelPromise = (async () => {
      // Dynamic import to keep TF.js out of the main bundle
      const tf = await import("@tensorflow/tfjs");
      const nsfwjs = await import("nsfwjs");
      // Use the MobileNetV2 mid-size model for good accuracy/speed balance
      return nsfwjs.load();
    })();
  }
  return modelPromise;
};

export interface ModerationResult {
  safe: boolean;
  reason?: string;
}

/**
 * Check if a File is appropriate for a business listing.
 * Returns { safe: true } or { safe: false, reason: "..." }.
 */
export const moderateImage = async (file: File): Promise<ModerationResult> => {
  try {
    const model = await loadModel();

    // Create an image element from the file
    const imageUrl = URL.createObjectURL(file);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = imageUrl;
    });

    const predictions = await model.classify(img);
    URL.revokeObjectURL(imageUrl);

    // Find prediction scores
    const scores: Record<string, number> = {};
    for (const p of predictions) {
      scores[p.className.toLowerCase()] = p.probability;
    }

    // Flag if Porn or Hentai exceeds threshold
    const pornScore = scores["porn"] || 0;
    const hentaiScore = scores["hentai"] || 0;
    const sexyScore = scores["sexy"] || 0;

    if (pornScore > 0.3) {
      return { safe: false, reason: "Image contains explicit content and cannot be uploaded." };
    }
    if (hentaiScore > 0.3) {
      return { safe: false, reason: "Image contains inappropriate illustrated content and cannot be uploaded." };
    }
    if (sexyScore > 0.6) {
      return { safe: false, reason: "Image may contain inappropriate content. Please use a more suitable photo for your business listing." };
    }

    return { safe: true };
  } catch (error) {
    console.error("Image moderation check failed:", error);
    // If moderation fails (e.g. model didn't load), allow the upload
    // rather than blocking the business from operating
    return { safe: true };
  }
};
